import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalyticsContext } from '../../../context/analytics-context/analytics-context';
import { useAppContext } from '../../../context/app-context';
import { setIsImageEditDrawerOpen } from '../../../context/app-context/app-context-actions';
import { useCardContext } from '../../../context/card-context';
import { useInitializationDataContext } from '../../../context/data-context/data-context';
import { CustomFabricImage } from '../../../global-types/canvas';
import { useFeatureFlags } from '../../../hooks';
import { useActiveCanvas } from '../../../hooks/use-active-canvas';
import { useActiveCardFace } from '../../../hooks/use-active-card-face';
import { useDatadog } from '../../../hooks/use-datadog';
import { deleteImage } from '../../../services/image';
import { CanvasDataTypes } from '../../../utils/canvas-utils/canvas-data-types';
import { addPhotozoneImageMoveConstraint } from '../utils/add-photozone-image-move-constraint';
import { applyImageForceFillConstraints } from '../utils/apply-image-force-fill-contraints';
import { deleteCardFacePhoto } from '../utils/delete-card-face-photo';
import { deletePhotozonePhoto } from '../utils/delete-photozone-photo';
import { getPhotoScale } from '../utils/get-photo-scale';
import { isFabricImage } from '../utils/is-fabric-image';
import { scalePhoto } from '../utils/scale-photo';

export const usePhotoControls = () => {
  const canvas = useActiveCanvas();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const projectId = initializedData?.project_id || '';
  const { cardState } = useCardContext();
  const currentPhoto = canvas?.current?.getActiveObject() as CustomFabricImage;
  const originalPhotoRef = useRef({ ...currentPhoto });
  const { updateEditFormats, trackDeleteElement } = useAnalyticsContext();
  const { appDispatch } = useAppContext();
  const { addAction } = useDatadog();
  const { t } = useTranslation();
  const { IS_FORCE_FILL_ENABLED } = useFeatureFlags();
  const { isFront } = useActiveCardFace();

  /**
   * Function to scale the selected photo on the canvas
   * This function scales the photo based on the provided scale factor and updates the canvas.
   * @param {number} scale - The scale factor to apply to the photo
   * @returns {void}
   */
  const scaleSelectedPhoto = (scale: number): void => {
    applyImageForceFillConstraints({ canvas, image: currentPhoto, isFront, scale });
    const newScale = scale / 100; // we use 100 to have the newScale from 0 to 1 and to handle the original size of the image.

    if (isFabricImage(currentPhoto) && canvas?.current && originalPhotoRef.current) {
      if (currentPhoto.data.scaleXOriginal === undefined || currentPhoto.data.scaleYOriginal === undefined) {
        currentPhoto.data.scaleXOriginal = originalPhotoRef.current.scaleX;
        currentPhoto.data.scaleYOriginal = originalPhotoRef.current.scaleY;
      }
      if (!currentPhoto.data) currentPhoto.data = {};
      currentPhoto.data.scaleValue = scale;

      scalePhoto({
        photo: currentPhoto,
        scaleFactor: newScale,
        canvas: canvas.current,
        originalScale: { x: currentPhoto.data.scaleXOriginal, y: currentPhoto.data.scaleYOriginal },
        isForceFillEnabled: IS_FORCE_FILL_ENABLED,
        isFront,
      });
    }
  };

  const getCurrentPhotoScale = (): number => getPhotoScale(currentPhoto);

  /**
   * Function to rotate the selected photo on the canvas
   * This function rotates the photo based on the provided angle by slider
   * @param {number} angle - The angle to rotate the photo
   * @returns {void}
   */
  const rotateSelectedPhoto = (angle: number): void => {
    if (isFabricImage(currentPhoto) && canvas?.current) {
      currentPhoto.rotate(angle);
      updateEditFormats({ rotate: angle.toString() });
      if (isFront && IS_FORCE_FILL_ENABLED) {
        const photozoneId = currentPhoto?.data?.photoZoneId;
        const photozoneObject = canvas?.current?.getObjects().find((obj) => obj.name === photozoneId);
        currentPhoto.off('moving');
        addPhotozoneImageMoveConstraint(currentPhoto as fabric.Image, photozoneObject as any).onCallback();
      }
      canvas.current.renderAll();
    }
  };

  /**
   * Function to handle deleting the selected photo
   * This function removes a photo from the canvas and the server.
   * it checks if the photo is part of a photo zone or a card face and calls the appropriate functionality.
   * @returns {Promise<void>} - A promise that resolves when the photo is deleted
   */
  const deleteSelectedPhoto = async (): Promise<void> => {
    if (isFabricImage(currentPhoto) && currentPhoto.name && canvas?.current) {
      const errorTitle = t('drawerContainer.imageErrorTitle');
      const errorMessage = t('drawerContainer.resetErrorMessage');
      if (currentPhoto.data?.type === CanvasDataTypes.PhotoZoneImage) {
        await deletePhotozonePhoto({
          currentPhoto,
          canvas: canvas.current,
          cardState,
          projectId,
          deleteImage,
          addAction,
          appDispatch,
          errorTitle,
          errorMessage,
        });
      } else {
        await deleteCardFacePhoto({
          currentPhoto,
          canvas: canvas.current,
          projectId,
          deleteImage,
          addAction,
          appDispatch,
          errorTitle,
          errorMessage,
        });
      }
      trackDeleteElement();
    }
  };

  /**
   * Function to handle the change of order of a photo
   * This function updates the order of the photo in the canvas and updates the edit formats.
   * @param {number} order - The new order to set for the selected photo
   * @returns {void}
   */
  const changePhotoOrder = (order: number, photo: fabric.Image): void => {
    if (isFabricImage(photo) && canvas?.current) {
      photo.moveTo(order);
      updateEditFormats({ order: order.toString() });
    }
  };

  /**
   * Function to handle photo selection
   * @returns {void}
   */
  const onPhotoSelect = (): void => {
    setIsImageEditDrawerOpen(appDispatch);
  };

  return {
    scaleSelectedPhoto,
    rotateSelectedPhoto,
    onPhotoSelect,
    currentPhoto,
    deleteSelectedPhoto,
    changePhotoOrder,
    getCurrentPhotoScale,
  };
};
